import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

// Real like counts + caller's liked state for a set of videos, plus
// fire-and-forget play logging. Backed by migration 023 RPCs.
// engagement: { [videoId]: { likeCount, liked, commentCount } } — missing ids
// mean 0/false. comment_count arrives from migration 033, folded into this
// same batched call rather than a second RPC per feed render.
export function useVideoEngagement(videoIds) {
  const [engagement, setEngagement] = useState({});
  // Video ids are UUIDs or small ints — never contain commas
  const idsKey = videoIds.map(String).join(',');

  useEffect(() => {
    if (!idsKey) return;
    let cancelled = false;

    (async () => {
      const { data, error } = await supabase.rpc('get_video_engagement', {
        p_video_ids: idsKey.split(','),
      });
      if (error) {
        console.error('get_video_engagement:', error);
        return;
      }
      if (cancelled) return;
      setEngagement((prev) => {
        const next = { ...prev };
        (data ?? []).forEach((row) => {
          next[row.video_id] = {
            likeCount: Number(row.like_count),
            liked: row.user_liked,
            commentCount: Number(row.comment_count ?? 0),
          };
        });
        return next;
      });
    })();

    return () => { cancelled = true; };
  }, [idsKey]);

  const toggleLike = useCallback(async (videoId) => {
    const id = String(videoId);
    const flip = (prev) => {
      const cur = prev[id] || { likeCount: 0, liked: false, commentCount: 0 };
      return {
        ...prev,
        [id]: {
          ...cur,   // keep commentCount — the like toggle must not blank it
          likeCount: Math.max(0, cur.likeCount + (cur.liked ? -1 : 1)),
          liked: !cur.liked,
        },
      };
    };
    setEngagement(flip); // optimistic
    const { error } = await supabase.rpc('toggle_video_like', { p_video_id: id });
    if (error) {
      console.error('toggle_video_like:', error);
      setEngagement(flip); // revert
    }
  }, []);

  const logPlay = useCallback((videoId) => {
    supabase
      .rpc('log_video_event', { p_video_id: String(videoId), p_event: 'play' })
      .then(({ error }) => {
        if (error) console.error('log_video_event:', error);
      });
  }, []);

  return { engagement, toggleLike, logPlay };
}
